import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Divider,
  Group,
  Menu,
  Modal,
  Select,
  Skeleton,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { motion } from 'framer-motion';
import {
  IconAlertCircle,
  IconChevronRight,
  IconDots,
  IconFileText,
  IconPlus,
  IconRobot,
  IconSettings,
  IconTrash,
} from '@tabler/icons-react';
import { trpc } from '../../lib/trpc';

type ChannelKey = 'whatsapp' | 'messenger' | 'instagram';
const CHANNELS: Array<{ key: ChannelKey; label: string }> = [
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'messenger', label: 'Messenger' },
  { key: 'instagram', label: 'Instagram' },
];

function AiAgentsIndex() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const agents = trpc.aiAgents.list.useQuery();
  const active = trpc.aiAgents.getActive.useQuery();
  const aiCfg = trpc.tenancy.ai.getConfig.useQuery();

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');

  const create = trpc.aiAgents.create.useMutation({
    onSuccess: (agent) => {
      void utils.aiAgents.list.invalidate();
      setCreateOpen(false);
      setNewName('');
      setNewDesc('');
      notifications.show({ message: 'Agent created', color: 'green' });
      void navigate({
        to: '/vendor/settings/ai-agents/$agentId',
        params: { agentId: String(agent.id) },
      });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const remove = trpc.aiAgents.delete.useMutation({
    onSuccess: () => {
      void utils.aiAgents.list.invalidate();
      void utils.aiAgents.getActive.invalidate();
      notifications.show({ message: 'Agent deleted', color: 'blue' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const setStatus = trpc.aiAgents.setStatus.useMutation({
    onSuccess: () => {
      void utils.aiAgents.list.invalidate();
      notifications.show({ message: 'Agent updated', color: 'green' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const confirmDelete = (id: number, name: string) =>
    modals.openConfirmModal({
      title: 'Delete agent?',
      centered: true,
      children: (
        <Text size="sm">
          This permanently deletes <b>{name}</b> and all its uploaded knowledge files. This cannot
          be undone.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => remove.mutate({ id }),
    });

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="xl">
        <Group justify="space-between" align="flex-start">
          <Stack gap={4}>
            <Group gap="sm">
              <IconRobot size={24} color="var(--mantine-color-blue-6)" />
              <Title order={2}>AI agents</Title>
            </Group>
            <Text c="dimmed" size="sm">
              Create AI agents with your business context and knowledge files. The active agent
              auto-replies to customers when no keyword rule matches.
            </Text>
          </Stack>
          <Button leftSection={<IconPlus size={16} />} onClick={() => setCreateOpen(true)}>
            New agent
          </Button>
        </Group>

        {aiCfg.data && !aiCfg.data.enabled && (
          <Alert color="orange" variant="light" icon={<IconAlertCircle size={16} />}>
            The AI responder is currently <b>off</b>, so agents won&apos;t auto-reply yet. Turn it
            on in{' '}
            <Text component={Link} to="/vendor/settings/ai-and-timing" fw={600} inherit>
              AI &amp; timing
            </Text>
            .
          </Alert>
        )}

        <ActiveResponderCard
          agents={agents.data ?? []}
          active={active.data}
          loading={agents.isPending || active.isPending}
        />

        <Stack gap="xs">
          <Title order={4}>Your agents</Title>
          {agents.isPending ? (
            <Skeleton height={120} />
          ) : agents.data && agents.data.length > 0 ? (
            agents.data.map((a) => {
              const activeChannels = active.data
                ? CHANNELS.filter((c) => active.data?.[c.key] === a.id).map((c) => c.label)
                : [];
              const isDefault = active.data?.default === a.id;
              return (
                <Card key={a.id} withBorder padding="md" radius="md">
                  <Group justify="space-between" wrap="nowrap">
                    <Group
                      gap="md"
                      wrap="nowrap"
                      onClick={() =>
                        navigate({
                          to: '/vendor/settings/ai-agents/$agentId',
                          params: { agentId: String(a.id) },
                        })
                      }
                      style={{ cursor: 'pointer', flex: 1, minWidth: 0 }}
                    >
                      <IconRobot size={20} color="var(--mantine-color-blue-6)" />
                      <Stack gap={2} style={{ minWidth: 0 }}>
                        <Group gap="xs">
                          <Text fw={600} truncate>
                            {a.name}
                          </Text>
                          <Badge
                            size="sm"
                            variant="light"
                            color={a.status === 1 ? 'green' : 'gray'}
                          >
                            {a.status === 1 ? 'active' : 'inactive'}
                          </Badge>
                          {isDefault && (
                            <Badge size="sm" variant="filled" color="blue">
                              default
                            </Badge>
                          )}
                          {activeChannels.map((c) => (
                            <Badge key={c} size="sm" variant="dot" color="blue">
                              {c}
                            </Badge>
                          ))}
                        </Group>
                        <Group gap="xs">
                          <Text c="dimmed" size="xs">
                            <IconFileText
                              size={12}
                              style={{ verticalAlign: 'middle', marginRight: 4 }}
                            />
                            {a.fileCount} file{a.fileCount === 1 ? '' : 's'}
                            {a.fileCount > 0 ? ` · ${a.readyFileCount} ready` : ''}
                          </Text>
                          {a.description && (
                            <Text c="dimmed" size="xs" truncate>
                              · {a.description}
                            </Text>
                          )}
                        </Group>
                      </Stack>
                    </Group>
                    <Group gap={4} wrap="nowrap">
                      <Menu position="bottom-end" withinPortal shadow="md">
                        <Menu.Target>
                          <ActionIcon variant="subtle" color="gray">
                            <IconDots size={18} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item
                            leftSection={<IconSettings size={16} />}
                            onClick={() =>
                              navigate({
                                to: '/vendor/settings/ai-agents/$agentId',
                                params: { agentId: String(a.id) },
                              })
                            }
                          >
                            Edit
                          </Menu.Item>
                          <Menu.Item
                            onClick={() =>
                              setStatus.mutate({ id: a.id, status: a.status === 1 ? '2' : '1' })
                            }
                          >
                            {a.status === 1 ? 'Deactivate' : 'Activate'}
                          </Menu.Item>
                          <Menu.Divider />
                          <Menu.Item
                            color="red"
                            leftSection={<IconTrash size={16} />}
                            onClick={() => confirmDelete(a.id, a.name)}
                          >
                            Delete
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                      <IconChevronRight size={18} color="var(--mantine-color-gray-5)" />
                    </Group>
                  </Group>
                </Card>
              );
            })
          ) : (
            <Card withBorder padding="xl" radius="md">
              <Stack align="center" gap="xs">
                <IconRobot size={32} color="var(--mantine-color-gray-5)" />
                <Text c="dimmed" size="sm">
                  No agents yet. Create one to give your AI responder a persona and knowledge.
                </Text>
                <Button
                  variant="light"
                  leftSection={<IconPlus size={16} />}
                  onClick={() => setCreateOpen(true)}
                >
                  New agent
                </Button>
              </Stack>
            </Card>
          )}
        </Stack>
      </Stack>

      <Modal opened={createOpen} onClose={() => setCreateOpen(false)} title="New AI agent" centered>
        <Stack gap="sm">
          <TextInput
            label="Name"
            placeholder="e.g. Sales Assistant"
            required
            value={newName}
            onChange={(e) => setNewName(e.currentTarget.value)}
          />
          <Textarea
            label="Description"
            description="Internal note — not shown to customers."
            placeholder="Handles product questions for the storefront"
            value={newDesc}
            onChange={(e) => setNewDesc(e.currentTarget.value)}
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              loading={create.isPending}
              disabled={!newName.trim()}
              onClick={() =>
                create.mutate({ name: newName.trim(), description: newDesc.trim() || undefined })
              }
            >
              Create
            </Button>
          </Group>
        </Stack>
      </Modal>
    </motion.div>
  );
}

type AgentListItem = { id: number; name: string; status: number };
type ActiveSel = {
  default: number | null;
  whatsapp: number | null;
  messenger: number | null;
  instagram: number | null;
};

function ActiveResponderCard({
  agents,
  active,
  loading,
}: {
  agents: AgentListItem[];
  active: ActiveSel | undefined;
  loading: boolean;
}) {
  const utils = trpc.useUtils();
  const [sel, setSel] = useState<ActiveSel>({
    default: null,
    whatsapp: null,
    messenger: null,
    instagram: null,
  });
  const [advanced, setAdvanced] = useState(false);

  useEffect(() => {
    if (active) setSel(active);
  }, [active]);

  const save = trpc.aiAgents.setActive.useMutation({
    onSuccess: () => {
      void utils.aiAgents.getActive.invalidate();
      void utils.aiAgents.list.invalidate();
      notifications.show({ message: 'Active responder saved', color: 'green' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  if (loading) return <Skeleton height={140} />;

  // Only active agents are selectable as responders.
  const options = agents
    .filter((a) => a.status === 1)
    .map((a) => ({ value: String(a.id), label: a.name }));
  const toId = (v: string | null) => (v ? Number(v) : null);

  return (
    <Card withBorder padding="lg" radius="md">
      <Stack gap="sm">
        <Group justify="space-between">
          <Stack gap={2}>
            <Title order={4}>Active responder</Title>
            <Text c="dimmed" size="xs">
              The agent that auto-replies. Set a default for all channels, or override per channel.
            </Text>
          </Stack>
        </Group>

        <Select
          label="Default agent"
          placeholder={options.length ? 'Select an agent' : 'No active agents yet'}
          data={options}
          clearable
          value={sel.default ? String(sel.default) : null}
          onChange={(v) => setSel((s) => ({ ...s, default: toId(v) }))}
        />

        <Button variant="subtle" size="xs" w="fit-content" onClick={() => setAdvanced((a) => !a)}>
          {advanced ? 'Hide' : 'Per-channel overrides'}
        </Button>
        {advanced && (
          <Stack gap="sm">
            <Divider label="Per-channel overrides (optional)" labelPosition="center" />
            {CHANNELS.map((c) => (
              <Select
                key={c.key}
                label={c.label}
                placeholder="Use default"
                data={options}
                clearable
                value={sel[c.key] ? String(sel[c.key]) : null}
                onChange={(v) => setSel((s) => ({ ...s, [c.key]: toId(v) }))}
              />
            ))}
          </Stack>
        )}

        <Group justify="flex-end">
          <Button loading={save.isPending} onClick={() => save.mutate(sel)}>
            Save
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

export const Route = createFileRoute('/vendor/settings/ai-agents/')({ component: AiAgentsIndex });
